"use client";

import { ChevronDown, ChevronUp, ChevronsUpDown, Bookmark } from "lucide-react";
import { useT } from "@/hooks/useT";

// Predexon outage: drop the Smart Money + Smart ROI columns entirely (header +
// col + cells) under the flag — same path non-PM sources already take — so the
// table stays aligned instead of showing empty SMART$/ROI columns.
const HIDE_PREDEXON = process.env.NEXT_PUBLIC_HIDE_PREDEXON_TABS === "true";

export type TableSortColumn =
  | "market"
  | "volume24hr"
  | "volume"
  | "expires"
  | "liquidity"
  | "change1h"
  | "change24h"
  | "change7d";
export type TableSortOrder = "asc" | "desc";

interface MarketsListTableHeaderProps {
  tableSortBy: TableSortColumn;
  setTableSortBy: (col: TableSortColumn) => void;
  tableSortOrder: TableSortOrder;
  setTableSortOrder: (order: TableSortOrder) => void;
  /** Source-aware column visibility: % change + Smart Money + Liquidity are PM-only. */
  effectiveSourceFilter: string;
}

/**
 * Table header for the markets list view. Sortable columns toggle order
 * on click; non-sortable columns (Price, Chart, Bookmark, Smart Money,
 * Smart ROI) just show static labels.
 *
 * Pulled out of MarketsPageContent.tsx where the same 100+ line block
 * was inline. Six props, all stable in the parent — this component is
 * dumb, the sort logic + persistence stays upstream.
 */
export function MarketsListTableHeader({
  tableSortBy,
  setTableSortBy,
  tableSortOrder,
  setTableSortOrder,
  effectiveSourceFilter,
}: MarketsListTableHeaderProps) {
  const t = useT();
  const isPM = effectiveSourceFilter === "polymarket";
  const isKalshi = effectiveSourceFilter === "kalshi";

  /** Toggle order if the column is already active, otherwise switch column with the given default order. */
  const handleSort = (col: TableSortColumn, defaultOrder: TableSortOrder = "desc") => {
    if (tableSortBy === col) {
      setTableSortOrder(tableSortOrder === "asc" ? "desc" : "asc");
    } else {
      setTableSortBy(col);
      setTableSortOrder(defaultOrder);
    }
  };

  /** Sort indicator: filled arrow when column active, hover-only ghost otherwise. */
  const sortIcon = (col: TableSortColumn) =>
    tableSortBy === col ? (
      tableSortOrder === "asc" ? <ChevronUp className="w-2.5 h-2.5" /> : <ChevronDown className="w-2.5 h-2.5" />
    ) : (
      <ChevronsUpDown className="w-2.5 h-2.5 opacity-0 group-hover:opacity-50" />
    );

  return (
    <thead className="sticky top-0 z-10 bg-gray-50/90 dark:bg-[#0f0f12]/90 backdrop-blur-md border-b border-gray-200 dark:border-[#1A1A1A]">
      <tr>
        {/* Market */}
        <th
          className="px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-900 dark:hover:text-white transition-colors group"
          onClick={() => handleSort("market", "asc")}
        >
          <div className="flex items-center gap-1">
            {t("table.market")}
            {sortIcon("market")}
          </div>
        </th>
        {/* Price */}
        <th className="px-3 py-2.5 text-center text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 whitespace-nowrap">{t("table.price")}</th>
        {/* % change columns — Polymarket only.
            Gamma's `oneHourPriceChange` / `oneDayPriceChange`
            cover 1h + 24h. 7d is derived from yesHistory. */}
        {isPM && (
          <th
            className="px-2 py-2.5 text-right text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-900 dark:hover:text-white transition-colors group whitespace-nowrap"
            onClick={() => handleSort("change1h")}
          >
            <div className="flex items-center justify-end gap-1">
              {t("table.change1h")}
              {sortIcon("change1h")}
            </div>
          </th>
        )}
        {isPM && (
          <th
            className="px-2 py-2.5 text-right text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-900 dark:hover:text-white transition-colors group whitespace-nowrap"
            onClick={() => handleSort("change24h")}
          >
            <div className="flex items-center justify-end gap-1">
              {t("table.change24h")}
              {sortIcon("change24h")}
            </div>
          </th>
        )}
        {isPM && (
          <th
            className="px-2 py-2.5 text-right text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-900 dark:hover:text-white transition-colors group whitespace-nowrap"
            onClick={() => handleSort("change7d")}
          >
            <div className="flex items-center justify-end gap-1">
              {t("table.change7d")}
              {sortIcon("change7d")}
            </div>
          </th>
        )}
        {/* Chart */}
        <th className="px-3 py-2.5 text-center text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 whitespace-nowrap">{t("table.chart7d")}</th>
        {/* Smart Money */}
        {isPM && !HIDE_PREDEXON && (
          <th className="px-3 py-2.5 text-center text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 whitespace-nowrap">{t("table.smartMoney")}</th>
        )}
        {/* Smart ROI */}
        {isPM && !HIDE_PREDEXON && (
          <th className="px-3 py-2.5 text-right text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 whitespace-nowrap">{t("table.roi")}</th>
        )}
        {/* 24h Vol */}
        <th
          className="px-3 py-2.5 text-right text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-900 dark:hover:text-white transition-colors group"
          onClick={() => handleSort("volume24hr")}
        >
          <div className="flex items-center justify-end gap-1">
            {isKalshi ? t("table.volume") : t("table.volume24h")}
            {sortIcon("volume24hr")}
          </div>
        </th>
        {/* Liquidity — PM only. Non-PM had this slot labelled "OI" but
            per Daisy's request OI is removed across the board for the
            list view. Kalshi cache had no liquidity data anyway, so
            non-PM drops the column entirely. */}
        {isPM && (
          <th
            className="px-3 py-2.5 text-right text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-900 dark:hover:text-white transition-colors group"
            onClick={() => handleSort("liquidity")}
          >
            <div className="flex items-center justify-end gap-1">
              {t("table.liquidity")}
              {sortIcon("liquidity")}
            </div>
          </th>
        )}
        {/* Total Vol (secondary) */}
        <th
          className="px-3 py-2.5 text-right text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-900 dark:hover:text-white transition-colors group"
          onClick={() => handleSort("volume")}
        >
          <div className="flex items-center justify-end gap-1">
            {t("table.total")}
            {sortIcon("volume")}
          </div>
        </th>
        {/* Bookmark */}
        <th className="px-1 py-2.5 text-center text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
          <Bookmark className="w-3 h-3 mx-auto" />
        </th>
      </tr>
    </thead>
  );
}
